import React from 'react';
import TaskOwnerFilter from '../../../change-impact-report-filters/TaskOwnerFilter.jsx';
import Filters from '../../../filters/index.jsx';
import { navigateToChangeImpact } from '../../../links/index.jsx';
import { PieOutlinedIcon } from '../../../icons/PieOutlinedIcon.jsx';
import DateRangeFilter from '../../../change-impact-report-filters/DateRangeFilter.jsx';
import OwnersFilter from '../../../change-impact-report-filters/OwnersFilter.jsx';
import TitleFilter from '../../../change-impact-report-filters/TitleFilter.jsx';
import ImpactTypeFilter from '../../../change-impact-report-filters/ImpactTypeFilter.jsx';
import ScaleFilter from '../../../change-impact-report-filters/ScaleFilter.jsx';
import PerceptionFilter from '../../../change-impact-report-filters/PerceptionFilter.jsx';
import NumberOfPeopleFilter from '../../../change-impact-report-filters/NumberOfPeopleFilter.jsx';
import ActionPlanStatusFilter from '../../../change-impact-report-filters/ActionPlanStatusFilter.jsx';
import ActionPlanTypeFilter from '../../../change-impact-report-filters/ActionPlanTypeFilter.jsx';
import TaskFilter from '../../../change-impact-report-filters/TaskFilter.jsx';
import TaskStatusFilter from '../../../change-impact-report-filters/TaskStatusFilter.jsx';
import { ParticipantsIcon } from '../../../icons/ParticipantsIcon.jsx';
import GroupsFilter from '../../../change-impact-report-filters/GroupsFilter.jsx';
import EmployeesFilter from '../../../change-impact-report-filters/EmployeesFilter.jsx';
import ExternalEntitiesFilter from '../../../change-impact-report-filters/ExternalEntitiesFilter.jsx';
import StakeholdersFilter from '../../../change-impact-report-filters/StakeholdersFilter.jsx';
import { isAuthorized } from '../../../AuthorizedAction.js';
import { resetFilters } from '../utils/index.js';

function ReportFilters({ query, resetDependencies, permissions, projectId }) {
  const filtersData = [
    {
      key: 'change-impact',
      title: 'Change Impact',
      icon: <PieOutlinedIcon />,
      filters: [
        {
          key: 'date-range',
          title: 'Change Impact Date Range',
          resetQueryParams: { startDate: null, endDate: null },
          component: (
            <DateRangeFilter
              type="project"
              startDate={query.startDate}
              endDate={query.endDate}
              navigateAction={navigateToChangeImpact}
            />
          ),
        },
        {
          key: 'owner',
          title: 'Change Impact Owner',
          resetQueryParams: { ownerIds: [] },
          component: (
            <OwnersFilter
              id={projectId}
              navigateAction={navigateToChangeImpact}
              selectedOwnerIds={query.ownerIds}
            />
          ),
        },
        {
          key: 'title',
          title: 'Change Impact Title',
          resetQueryParams: { title: [] },
          component: (
            <TitleFilter
              title={query.title}
              navigateAction={navigateToChangeImpact}
            />
          ),
        },
        {
          key: 'impact-type',
          title: 'Impact Type',
          resetQueryParams: { changeImpactTypeIds: [] },
          component: (
            <ImpactTypeFilter
              navigateAction={navigateToChangeImpact}
              selectedImpactTypes={query.changeImpactTypeIds}
            />
          ),
        },
        {
          key: 'scale',
          title: 'Scale',
          resetQueryParams: { scale: [] },
          component: (
            <ScaleFilter
              navigateAction={navigateToChangeImpact}
              selectedScale={query.scale}
            />
          ),
        },
        {
          key: 'perception',
          title: 'Perception',
          resetQueryParams: { perceptions: [] },
          component: (
            <PerceptionFilter
              navigateAction={navigateToChangeImpact}
              selectedPerceptions={query.perceptions}
            />
          ),
        },
        {
          key: 'number-of-people',
          title: 'Number of People Impacted',
          resetQueryParams: { peopleImpacted: null },
          component: (
            <NumberOfPeopleFilter
              navigateAction={navigateToChangeImpact}
              peopleImpacted={query.peopleImpacted}
            />
          ),
        },
        {
          key: 'plan-status',
          title: 'Action Plan Status',
          resetQueryParams: { actionPlanStatuses: [] },
          component: (
            <ActionPlanStatusFilter
              navigateAction={navigateToChangeImpact}
              selectedPlanStatuses={query.actionPlanStatuses}
            />
          ),
        },
        {
          key: 'plan-type',
          title: 'Action Plan Type',
          resetQueryParams: { actionPlanTypes: [] },
          component: (
            <ActionPlanTypeFilter
              navigateAction={navigateToChangeImpact}
              selectedActionPlanTypes={query.actionPlanTypes}
            />
          ),
        },
        {
          key: 'tasks-title',
          title: 'Task Title',
          resetQueryParams: { taskIds: [] },
          component: (
            <TaskFilter
              id={projectId}
              navigateAction={navigateToChangeImpact}
              selectedTaskIds={query.taskIds}
            />
          ),
        },
        {
          key: 'task-status',
          title: 'Task Status',
          resetQueryParams: { taskStatuses: [] },
          component: (
            <TaskStatusFilter
              navigateAction={navigateToChangeImpact}
              selectedTaskStatuses={query.taskStatuses}
            />
          ),
        },
        {
          key: 'task-owner',
          title: 'Task Owner',
          resetQueryParams: { taskOwnerIds: [] },
          component: (
            <TaskOwnerFilter
              id={projectId}
              navigateAction={navigateToChangeImpact}
              selectedTaskOwnerIds={query.taskOwnerIds}
            />
          ),
        },
      ],
    },
    {
      key: 'who-is-impacted',
      title: 'Who is Impacted',
      icon: <ParticipantsIcon />,
      filters: [
        {
          key: 'impacted-groups',
          title: 'Group, Team, Department Impacted',
          resetQueryParams: { impactedGroupsIds: [] },
          component: (
            <GroupsFilter
              id={projectId}
              navigateAction={navigateToChangeImpact}
              selectedGroupsIds={query.impactedGroupsIds}
            />
          ),
        },
        {
          key: 'people-impacted',
          title: 'People Impacted',
          resetQueryParams: { impactedEmployeesIds: [] },
          component: (
            <EmployeesFilter
              id={projectId}
              navigateAction={navigateToChangeImpact}
              selectedEmployeesIds={query.impactedEmployeesIds}
            />
          ),
        },
        {
          key: 'external-impacted',
          title: 'External People, Group, Company, Vendor',
          resetQueryParams: { impactedExternalEntitiesIds: [] },
          component: (
            <ExternalEntitiesFilter
              id={projectId}
              navigateAction={navigateToChangeImpact}
              selectedImpactedExternalEntitiesIds={
                query.impactedExternalEntitiesIds
              }
            />
          ),
        },
        {
          key: 'org-stakeholder',
          title: 'Stakeholder Group / Individual',
          disabled: !isAuthorized(
            'read',
            'organizationStakeholder',
            permissions
          ),
          resetQueryParams: { organizationStakeholderIds: [] },
          component: (
            <StakeholdersFilter
              id={projectId}
              navigateAction={navigateToChangeImpact}
              selectedStakeholdersIds={query.organizationStakeholderIds}
            />
          ),
        },
      ],
    },
  ];

  return (
    <Filters
      filtersData={filtersData}
      query={query}
      resetDependencies={resetDependencies}
      resetAction={navigateToChangeImpact}
      resetAllAction={resetFilters}
    />
  );
}

export default ReportFilters;
